import React from 'react';
import { MdAdd, MdCheck } from 'react-icons/md';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslations } from 'next-intl';

interface PropTypes {
  btnType: 'text' | 'icon';
  watchCount?: number;
  isSmall?: boolean;
  withOptions?: boolean;
  isWatched: boolean;
  disabled?: boolean;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
  onRewatch?: (event?: React.MouseEvent<HTMLElement>) => void;
  onResetWatches?: (event?: React.MouseEvent<HTMLElement>) => void;
  onResetRewatches?: (event?: React.MouseEvent<HTMLElement>) => void;
}

function WatchedButton({
  btnType,
  watchCount,
  isSmall = false,
  withOptions = false,
  isWatched,
  disabled = false,
  onClick,
  onRewatch,
  onResetWatches,
  onResetRewatches
}: PropTypes) {
  const t = useTranslations();

  return (
    <>
      {!withOptions && btnType === 'icon' && isSmall && (
        <>
          <button
            onClick={onClick}
            className={`flex flex-row items-center justify-center w-[40px] h-[40px] rounded-full font-bold px-4 ${
              isWatched
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-white hover:bg-gray-200 text-black'
            }`}
            disabled={disabled}
          >
            <MdCheck
              className={`${isWatched ? 'text-white' : 'text-black'}`}
              size={18}
            />
          </button>
        </>
      )}

      {!withOptions && btnType === 'icon' && !isSmall && (
        <button
          onClick={onClick}
          disabled={disabled}
          className={`flex flex-row items-center justify-center w-[40px] h-[40px] rounded-full font-bold px-2 ${
            isWatched
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-white hover:bg-gray-200 text-black'
          }`}
        >
          <MdCheck
            className={`${isWatched ? 'text-white' : 'text-black'}`}
            size={18}
          />
        </button>
      )}

      {!withOptions && btnType === 'text' && (
        <button
          onClick={onClick}
          className={`flex flex-row items-center h-[40px] rounded-md font-bold px-4 ${
            isWatched
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-red-600 hover:bg-red-500 text-black'
          }`}
        >
          {isWatched ? (
            <MdCheck size={20} className="text-white" />
          ) : (
            <MdAdd size={20} className="text-white" />
          )}

          <span className="text-white font-bold ml-2">
            {isWatched
              ? t('components.watched_button.texts.unmark_as_watched')
              : t('components.watched_button.texts.mark_as_watched')}
          </span>
        </button>
      )}

      {withOptions && (
        <DropdownMenu.Root>
          {btnType === 'icon' && (
            <DropdownMenu.Trigger
              className={`flex flex-row items-center justify-center w-[40px] h-[40px] rounded-full font-bold px-2  ${
                isWatched
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-white hover:bg-gray-200 text-black'
              }`}
            >
              {watchCount ? (
                <span className="text-white font-bold text-xs">
                  {watchCount}x
                </span>
              ) : (
                <MdCheck
                  className={`${isWatched ? 'text-white' : 'text-black'}`}
                  size={18}
                />
              )}
            </DropdownMenu.Trigger>
          )}

          {btnType === 'text' && (
            <DropdownMenu.Trigger
              className={`h-[40px] rounded-md font-bold px-4 ${
                isWatched
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-white hover:bg-gray-200 text-black'
              }`}
            >
              {isWatched && watchCount ? (
                <span className="text-white font-bold">{watchCount}x</span>
              ) : (
                <MdAdd size={20} className="text-white" />
              )}

              <span className="text-white font-bold ml-2">
                {isWatched
                  ? t('components.watched_button.texts.unmark_as_watched')
                  : t('components.watched_button.texts.mark_as_watched')}
              </span>
            </DropdownMenu.Trigger>
          )}

          <DropdownMenu.Portal style={{ zIndex: 100 }} className="z-10">
            <DropdownMenu.Content
              align="end"
              sideOffset={5}
              className={
                'w-48 rounded-lg p-2 shadow-md md:w-56 bg-white dark:bg-black z-10'
              }
            >
              <DropdownMenu.Item
                className={
                  'flex items-center rounded-md px-2 h-10 text-xs outline-none focus:bg-gray-200 cursor-pointer'
                }
              >
                <span
                  onClick={onRewatch}
                  className="flex-grow text-white text-sm"
                >
                  {t('components.watched_button.options.rewatched')}
                </span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className={
                  'flex items-center rounded-md px-2 h-10 text-xs outline-none focus:bg-gray-200 cursor-pointer'
                }
              >
                <span
                  onClick={onResetWatches}
                  className="flex-grow text-white text-sm"
                >
                  {t('components.watched_button.options.not_watched')}
                </span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className={
                  'flex items-center rounded-md px-2 h-10 text-xs outline-none focus:bg-gray-200 cursor-pointer'
                }
              >
                <span
                  onClick={onResetRewatches}
                  className="flex-grow text-white text-sm"
                >
                  {t('components.watched_button.options.watched_once')}
                </span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </>
  );
}

export default WatchedButton;
